import { useCallback, useMemo, useState } from 'react';
import { useEditPhieuChiWithUpdatedInvoice, useMultipleForms, useSearchFieldStates, useSearchTable } from '../hooks';
import { getDataTableColumns, getInputTableColumns } from '../cols-definition';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { initialSearchValues, SearchFormValues } from '../schema';
import { usePhieuChiSearchContext } from './search-context';
import type { PhieuChiFormInitialData } from '../types';
import { PhieuChi } from '@/types/schemas';
import { useForms } from '@/hooks';

export const usePhieuChiTable = () => {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const shared = usePhieuChiSearchContext();
  const form = useForms<PhieuChi>(shared.fetchDetail);
  const multipleForms = useMultipleForms({ addPhieuChi: shared.addItem });
  const editWithUpdatedInvoice = useEditPhieuChiWithUpdatedInvoice();
  const searchFieldStates = useSearchFieldStates();
  const searchTable = useSearchTable({
    searchData: shared.searchData,
    searchParams,
    setSearchParams,
    handleViewClick: form.handleViewClick,
    sumTotal: shared.sumTotal,
    isFiltered: shared.isFiltered,
    handlePageChange: shared.handlePageChange
  });

  const handleSearchOpen = useCallback(() => {
    setShowSearchDialog(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearchDialog(false);
  }, []);

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      setSearchParams(filters);
      await searchTable.handleInitialSearch(filters);
      setShowSearchDialog(false);
    },
    [searchTable]
  );

  const handleRefresh = useCallback(async () => {
    if (searchTable.hasFilterParams) {
      await searchTable.handleRefreshClick();
    } else if (shared.isFiltered) {
      await shared.searchData(searchParams);
    } else {
      await shared.refreshData();
    }

    if (form.selectedObj?.uuid) {
      await shared.fetchDetail(form.selectedObj.uuid);
    }
  }, [form.selectedObj?.uuid, searchParams, searchTable, shared]);

  const displayData = useMemo(
    () => (searchTable.hasFilterParams ? searchTable.searchState.searchResults : shared.data || []),
    [searchTable.hasFilterParams, searchTable.searchState.searchResults, shared.data]
  );
  const totalAmount = searchTable.hasFilterParams
    ? (searchTable.searchState.totalRow?.t_tien ?? shared.sumTotal)
    : shared.sumTotal;
  const columns = useMemo(
    () => getDataTableColumns(form.handleViewClick, totalAmount) as DataTableColumn[],
    [form.handleViewClick, totalAmount]
  );
  const tables = useMemo(
    () => [
      {
        name: 'Tất cả',
        rows: displayData,
        columns
      }
    ],
    [columns, displayData]
  );
  return {
    shared,
    form,
    searchDialog: {
      open: showSearchDialog,
      searchParams,
      searchFieldStates,
      onOpen: handleSearchOpen,
      onClose: handleSearchClose,
      onSubmit: handleSearchSubmit
    },
    multipleFormDialog: {
      open: multipleForms.showMultipleForms,
      initialData: multipleForms.initialData,
      invoiceData: multipleForms.invoiceData,
      onSubmit: multipleForms.handleMultipleFormSubmit,
      onClose: multipleForms.handleCloseMultipleForms
    },
    editWithUpdatedDialog: {
      open: editWithUpdatedInvoice.shouldEditWithUpdatedData,
      detail: editWithUpdatedInvoice.mergedFormData,
      uuid: editWithUpdatedInvoice.editPhieuChiUuid,
      isLoading: editWithUpdatedInvoice.isLoading
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading: shared.isLoading || shared.isSearching || searchTable.isFiltering,
      currentPage: searchTable.hasFilterParams ? searchTable.searchState.currentPage : shared.currentPage,
      totalItems: searchTable.hasFilterParams ? searchTable.searchState.totalCount : shared.totalItems,
      onPageChange: searchTable.onPageChange,
      onSearchChange: searchTable.handleSearchChange,
      onRefresh: handleRefresh
    },
    detailTable: {
      rows: form.selectedObj ? (shared.detail as PhieuChiFormInitialData | null | undefined)?.chi_tiet || [] : [],
      columns: getInputTableColumns()
    },
    selection: {
      selectedObj: form.selectedObj,
      selectedRowIndex: form.selectedRowIndex,
      clearSelection: form.clearSelection
    }
  };
};
